import { useEffect, useRef, useCallback, useMemo } from 'react';
import { Howl } from 'howler';

/**
 * Custom hook for preloading and playing sounds instantly
 * @param {string[]} soundPaths - Array of sound file paths (relative to public/sounds/)
 * @param {boolean} enabled - Whether to preload and enable sound playback (default: true)
 * @returns {Object} - Object containing playSound function
 */
export const useSounds = (soundPaths = [], enabled = true) => {
  const soundsRef = useRef(new Map());
  const loadedRef = useRef(new Set());
  
  // Memoize soundPaths to prevent unnecessary re-initialization
  const memoizedSoundPaths = useMemo(() => soundPaths, [soundPaths.join(',')]);

  useEffect(() => {
    // Clear existing sounds when soundPaths change
    soundsRef.current.forEach(howl => howl.unload());
    soundsRef.current.clear();
    loadedRef.current.clear();

    // Only preload sounds if enabled
    if (!enabled) {
      return;
    }

    // Preload all sounds
    memoizedSoundPaths.forEach(soundPath => {
      // Extract filename without extension for the key
      const soundKey = soundPath.split('/').pop().split('.')[0];
      
      const howl = new Howl({
        src: [`/sounds/${soundPath}`],
        preload: true,
        volume: 1.0,
        onload: () => {
          loadedRef.current.add(soundKey);
        },
        onloaderror: (id, error) => {
          console.warn(`Failed to load sound: ${soundPath}`, error);
        }
      });

      soundsRef.current.set(soundKey, howl);
      soundsRef.current.set(soundPath, howl); // Also store by full path for flexibility
    });

    // Cleanup function
    return () => {
      soundsRef.current.forEach(howl => howl.unload());
      soundsRef.current.clear();
      loadedRef.current.clear();
    };
  }, [memoizedSoundPaths, enabled]);

  /**
   * Play a sound instantly (if preloaded)
   * @param {string} soundName - Sound name (filename without extension) or full path
   * @param {Object} options - Optional playback options
   * @param {number} options.volume - Volume (0.0 to 1.0)
   * @param {number} options.rate - Playback rate (0.5 to 4.0)
   * @param {boolean} options.loop - Whether to loop the sound
   */
  const playSound = useCallback((soundName, options = {}) => {
    const howl = soundsRef.current.get(soundName);
    
    if (!howl) {
      console.warn(`Sound not found: ${soundName}. Available sounds:`, Array.from(soundsRef.current.keys()));
      return;
    }

    // Apply options if provided
    if (options.volume !== undefined) {
      howl.volume(options.volume);
    }
    if (options.rate !== undefined) {
      howl.rate(options.rate);
    }
    if (options.loop !== undefined) {
      howl.loop(options.loop);
    }

    // Play the sound
    howl.play();
  }, []);

  /**
   * Stop a specific sound
   * @param {string} soundName - Sound name or path to stop
   */
  const stopSound = useCallback((soundName) => {
    const howl = soundsRef.current.get(soundName);
    if (howl) {
      howl.stop();
    }
  }, []);

  /**
   * Stop all sounds
   */
  const stopAllSounds = useCallback(() => {
    soundsRef.current.forEach(howl => howl.stop());
  }, []);

  /**
   * Check if a sound is loaded and ready to play
   * @param {string} soundName - Sound name to check
   * @returns {boolean} - Whether the sound is loaded
   */
  const isSoundLoaded = useCallback((soundName) => {
    const soundKey = soundName.split('/').pop().split('.')[0];
    return loadedRef.current.has(soundKey);
  }, []);

  /**
   * Get loading status of all sounds
   * @returns {Object} - Object with sound names as keys and loading status as values
   */
  const getLoadingStatus = useCallback(() => {
    const status = {};
    memoizedSoundPaths.forEach(soundPath => {
      const soundKey = soundPath.split('/').pop().split('.')[0];
      status[soundKey] = loadedRef.current.has(soundKey);
    });
    return status;
  }, [memoizedSoundPaths]);

  return {
    playSound,
    stopSound,
    stopAllSounds,
    isSoundLoaded,
    getLoadingStatus
  };
};
